import { useEffect, useMemo } from 'react';

import {
	deserializeState,
	useSetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import type { GetOnboardingState } from '@confluence/onboarding-helpers/entry-points/getOnboardingStateGQL.ts';
import { PERSONALIZED_TEMPLATES_V2_ONBOARDING_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';

import type {
	OnboardingActionId,
	OnboardingActionState,
	OnboardingActionStates,
	OnboardingAction,
	GettingStartedCardType,
} from '../PersonalizedTemplatesV2Types.ts';

interface UsePersonalizedTemplateOnboardingActionsHook {
	actions: OnboardingAction[];
	dismiss: (id: OnboardingActionId) => void;
	complete: (id: OnboardingActionId) => void;
	hasDismissedOnboardingActions: boolean;
}

function deserializeOnboardingActionStates(
	serializedValues: string | undefined,
	OnboardingActionCards: GettingStartedCardType[],
): OnboardingActionStates {
	try {
		const values = JSON.parse(serializedValues || '{}');
		if (typeof values !== 'object') {
			return {};
		}
		const ONBOARDING_ACTION_IDS = OnboardingActionCards.map((action) => action.id);
		// Return a mapping of all onboarding action IDs to their states.
		const states = ONBOARDING_ACTION_IDS.reduce(
			(states, id) => ({ ...states, [id]: values[id] }),
			{} as OnboardingActionStates,
		);
		return states;
	} catch (e) {
		return {};
	}
}

function serializeOnboardingActionStates(onboardingActionStates: OnboardingActionStates): string {
	return JSON.stringify(onboardingActionStates);
}

export function usePersonalizedTemplateOnboardingActions(
	HomeOnboardingTakeoverCardsList: GettingStartedCardType[],
	onboardingStateData?: GetOnboardingState,
	onboardingActionStatesKey: string = PERSONALIZED_TEMPLATES_V2_ONBOARDING_STATE_KEYS.onboardingActionStates,
): UsePersonalizedTemplateOnboardingActionsHook {
	const { setOnboardingState } = useSetOnboardingState();
	const {
		onboardingActionStates: serializedOnboardingActionStates,
		hasUserPublished,
		hasDismissedOnboardingActions,
	} = deserializeState(onboardingStateData);

	const onboardingActionStates = useMemo(() => {
		return deserializeOnboardingActionStates(
			serializedOnboardingActionStates,
			HomeOnboardingTakeoverCardsList,
		);
	}, [serializedOnboardingActionStates, HomeOnboardingTakeoverCardsList]);

	const actions: OnboardingAction[] = useMemo(() => {
		const mappedActions = HomeOnboardingTakeoverCardsList.map((action) => ({
			id: action.id,
			state: onboardingActionStates[action.id] || 'pending',
		}));
		return mappedActions;
	}, [HomeOnboardingTakeoverCardsList, onboardingActionStates]);

	const dismiss = useMemo(
		() => (id: OnboardingActionId) => {
			const newState: OnboardingActionStates = {
				...onboardingActionStates,
				[id]: 'dismissed' as OnboardingActionState,
			};
			setOnboardingState({
				key: onboardingActionStatesKey,
				value: serializeOnboardingActionStates(newState),
			});
		},
		[onboardingActionStates, setOnboardingState, onboardingActionStatesKey],
	);

	const complete = useMemo(
		() => (id: OnboardingActionId) => {
			const newState: OnboardingActionStates = {
				...onboardingActionStates,
				[id]: 'completed' as OnboardingActionState,
			};
			setOnboardingState({
				key: onboardingActionStatesKey,
				value: serializeOnboardingActionStates(newState),
			});
		},
		[onboardingActionStates, setOnboardingState, onboardingActionStatesKey],
	);

	useEffect(() => {
		const hasntCreatedPage = actions.some(
			(action) => action.id === 'createPage' && action.state === 'pending',
		);

		if (hasUserPublished && hasntCreatedPage) {
			complete('createPage');
		}
	}, [hasUserPublished, complete, actions]);

	return {
		actions,
		dismiss,
		complete,
		hasDismissedOnboardingActions,
	};
}
